import { Service, inject } from '@angular/core';
import { TranslateService, TranslationObject } from '@ngx-translate/core';
import { SaphirModuleDefinition } from '@saphir/module-contract';

import { SUPPORTED_LANGUAGES } from '../core/i18n/language';

/**
 * Merges a module's own dictionary into the shell's.
 *
 * Every shipped language is loaded, not just the active one: the header
 * switcher changes the language at runtime without a reload, and ngx-translate
 * resolves a key missing from the active language through the fallback
 * language, so a module whose German is incomplete shows English there instead
 * of a raw key — the same behaviour the shell's own dictionary gets. Two small
 * files per module are cheaper than a re-merge on every switch.
 */
@Service()
export class ModuleTranslationsService {
  private readonly translate = inject(TranslateService);

  /**
   * Loads and merges the module's dictionaries.
   *
   * Returns what went wrong, if anything. A module with no usable dictionary
   * still works — its keys render as themselves, which is bad but is not a
   * reason to withhold a functioning module.
   */
  async load(definition: SaphirModuleDefinition): Promise<string[]> {
    const { loadTranslations, manifest } = definition;

    if (!loadTranslations) {
      return [];
    }

    const problems: string[] = [];

    for (const language of SUPPORTED_LANGUAGES) {
      try {
        const dictionary = await loadTranslations(language);
        const problem = this.merge(manifest.id, language, dictionary);

        if (problem) {
          problems.push(problem);
        }
      } catch (error) {
        problems.push(
          `Module "${manifest.id}" has no ${language} translations: ${describeError(error)}.`,
        );
      }
    }

    return problems;
  }

  /**
   * Everything a module contributes has to sit under its own id.
   *
   * Enforced rather than assumed: a module shipping a `dashboard` key would
   * otherwise silently rewrite the shell's own labels, and a second module
   * could rewrite the first one's. Rejecting the whole dictionary is the safe
   * answer — a module with untranslated keys is a nuisance, a shell with
   * rewritten ones is a bug nobody will trace back to here.
   */
  private merge(id: string, language: string, dictionary: unknown): string | undefined {
    if (typeof dictionary !== 'object' || dictionary === null || Array.isArray(dictionary)) {
      return `Module "${id}" returned no ${language} dictionary object.`;
    }

    const roots = Object.keys(dictionary);
    const foreign = roots.filter((root) => root !== id);

    if (foreign.length > 0) {
      return (
        `Ignored the ${language} dictionary of module "${id}": it must contain nothing but the ` +
        `"${id}" namespace, and it also declares ${foreign.map((root) => `"${root}"`).join(', ')}.`
      );
    }

    // The contract types a dictionary as plain JSON; ngx-translate wants its own
    // shape for it. The structure was checked above, so this only renames it.
    this.translate.setTranslation(language, dictionary as TranslationObject, true);

    return undefined;
  }
}

function describeError(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
